<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="connectivity-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('connectivity.header.badge') }}</div>
                    <h2>{{ $t('connectivity.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('connectivity.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="connectivity-container">
                <!-- Architecture Flow -->
                <div class="architecture-section">
                    <div class="section-header">
                        <h3>{{ $t('connectivity.architecture.title') }}</h3>
                        <p>{{ $t('connectivity.architecture.subtitle') }}</p>
                    </div>

                    <div class="flow-wrapper">
                        <!-- User Layer -->
                        <div class="flow-level">
                            <div class="level-badge">
                                <span class="badge-number">{{ $t('connectivity.levels.user') }}</span>
                                <span class="badge-text">{{ $t('connectivity.architecture.userLayer.badge') }}</span>
                            </div>
                            <div class="flow-card user-card">
                                <div class="card-icon-wrapper">
                                    <v-icon size="40" color="white">mdi-account-group</v-icon>
                                </div>
                                <div class="card-content">
                                    <h4>{{ $t('connectivity.architecture.userLayer.title') }}</h4>
                                    <p>{{ $t('connectivity.architecture.userLayer.description') }}</p>
                                    <div class="card-features">
                                        <div class="feature">
                                            <v-icon size="16">mdi-shield-check</v-icon>
                                            <span>{{ $t('connectivity.architecture.userLayer.features.mfa') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-account-key</v-icon>
                                            <span>{{ $t('connectivity.architecture.userLayer.features.rbac') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-login-variant</v-icon>
                                            <span>{{ $t('connectivity.architecture.userLayer.features.sso') }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Connection Arrow -->
                            <div class="flow-connector">
                                <div class="connector-line"></div>
                                <div class="connector-label">
                                    <v-icon size="16">mdi-lock</v-icon>
                                    <span>{{ $t('connectivity.architecture.userLayer.connector') }}</span>
                                </div>
                                <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                            </div>
                        </div>

                        <!-- Application Layer -->
                        <div class="flow-level">
                            <div class="level-badge">
                                <span class="badge-number">{{ $t('connectivity.levels.app') }}</span>
                                <span class="badge-text">{{ $t('connectivity.architecture.appLayer.badge') }}</span>
                            </div>
                            <div class="flow-card app-card">
                                <div class="card-icon-wrapper">
                                    <v-icon size="40" color="white">mdi-application-brackets-outline</v-icon>
                                </div>
                                <div class="card-content">
                                    <h4>{{ $t('connectivity.architecture.appLayer.title') }}</h4>
                                    <p>{{ $t('connectivity.architecture.appLayer.description') }}</p>
                                    <div class="card-features">
                                        <div class="feature">
                                            <v-icon size="16">mdi-responsive</v-icon>
                                            <span>{{ $t('connectivity.architecture.appLayer.features.responsive') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-cloud-sync</v-icon>
                                            <span>{{ $t('connectivity.architecture.appLayer.features.realtime') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-flash</v-icon>
                                            <span>{{ $t('connectivity.architecture.appLayer.features.offline') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-cached</v-icon>
                                            <span>{{ $t('connectivity.architecture.appLayer.features.caching') }}</span>
                                        </div>
                                    </div>
                                    <div class="tech-stack">
                                        <span 
                                            v-for="(tech, index) in techStackArray" 
                                            :key="index" 
                                            class="tech-badge"
                                        >
                                            {{ tech }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Connection Arrow -->
                            <div class="flow-connector">
                                <div class="connector-line"></div>
                                <div class="connector-label">
                                    <v-icon size="16">mdi-api</v-icon>
                                    <span>{{ $t('connectivity.architecture.appLayer.connector') }}</span>
                                </div>
                                <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                            </div>
                        </div>

                        <!-- Platform Layer -->
                        <div class="flow-level">
                            <div class="level-badge">
                                <span class="badge-number">{{ $t('connectivity.levels.platform') }}</span>
                                <span class="badge-text">{{ $t('connectivity.architecture.platformLayer.badge') }}</span>
                            </div>
                            <div class="flow-card platform-card">
                                <div class="card-icon-wrapper">
                                    <v-icon size="40" color="white">mdi-server-network</v-icon>
                                </div>
                                <div class="card-content">
                                    <h4>{{ $t('connectivity.architecture.platformLayer.title') }}</h4>
                                    <p>{{ $t('connectivity.architecture.platformLayer.description') }}</p>
                                    <div class="card-features">
                                        <div class="feature">
                                            <v-icon size="16">mdi-cube-outline</v-icon>
                                            <span>{{ $t('connectivity.architecture.platformLayer.features.microservices') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-scale-balance</v-icon>
                                            <span>{{ $t('connectivity.architecture.platformLayer.features.loadBalancing') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-backup-restore</v-icon>
                                            <span>{{ $t('connectivity.architecture.platformLayer.features.backups') }}</span>
                                        </div>
                                        <div class="feature">
                                            <v-icon size="16">mdi-chart-line</v-icon>
                                            <span>{{ $t('connectivity.architecture.platformLayer.features.monitoring') }}</span>
                                        </div>
                                    </div>
                                    <div class="platform-metrics">
                                        <div class="metric">
                                            <span class="metric-value">99.99%</span>
                                            <span class="metric-label">{{ $t('connectivity.architecture.platformLayer.metrics.uptime') }}</span>
                                        </div>
                                        <div class="metric">
                                            <span class="metric-value">50ms</span>
                                            <span class="metric-label">{{ $t('connectivity.architecture.platformLayer.metrics.latency') }}</span>
                                        </div>
                                        <div class="metric">
                                            <span class="metric-value">256-bit</span>
                                            <span class="metric-label">{{ $t('connectivity.architecture.platformLayer.metrics.encryption') }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Integration Systems -->
                <div class="systems-section">
                    <div class="section-header">
                        <h3>{{ $t('connectivity.integrations.title') }}</h3>
                        <p>{{ $t('connectivity.integrations.subtitle') }}</p>
                    </div>
                    
                    <v-row class="systems-grid">
                        <!-- Microsoft Ecosystem -->
                        <v-col cols="12" lg="3" md="6" class="pa-3">
                            <div class="system-card microsoft">
                                <div class="system-icon">
                                    <v-icon size="36" color="white">mdi-microsoft</v-icon>
                                </div>
                                <h4>{{ $t('connectivity.integrations.microsoft.title') }}</h4>
                                <p>{{ $t('connectivity.integrations.microsoft.description') }}</p>
                                <div class="integration-list">
                                    <div 
                                        v-for="(item, index) in microsoftItems" 
                                        :key="index" 
                                        class="integration-item"
                                    >
                                        <v-icon size="14">mdi-check-circle</v-icon>
                                        <span>{{ item }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Enterprise Systems -->
                        <v-col cols="12" lg="3" md="6" class="pa-3">
                            <div class="system-card enterprise">
                                <div class="system-icon">
                                    <v-icon size="36" color="white">mdi-domain</v-icon>
                                </div>
                                <h4>{{ $t('connectivity.integrations.enterprise.title') }}</h4>
                                <p>{{ $t('connectivity.integrations.enterprise.description') }}</p>
                                <div class="integration-list">
                                    <div 
                                        v-for="(item, index) in enterpriseItems" 
                                        :key="index" 
                                        class="integration-item"
                                    >
                                        <v-icon size="14">mdi-check-circle</v-icon>
                                        <span>{{ item }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- IoT & Smart Building -->
                        <v-col cols="12" lg="3" md="6" class="pa-3">
                            <div class="system-card iot">
                                <div class="system-icon">
                                    <v-icon size="36" color="white">mdi-chip</v-icon>
                                </div>
                                <h4>{{ $t('connectivity.integrations.iot.title') }}</h4>
                                <p>{{ $t('connectivity.integrations.iot.description') }}</p>
                                <div class="integration-list">
                                    <div 
                                        v-for="(item, index) in iotItems" 
                                        :key="index" 
                                        class="integration-item"
                                    >
                                        <v-icon size="14">mdi-check-circle</v-icon>
                                        <span>{{ item }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Third-Party Apps -->
                        <v-col cols="12" lg="3" md="6" class="pa-3">
                            <div class="system-card third-party">
                                <div class="system-icon">
                                    <v-icon size="36" color="white">mdi-puzzle-outline</v-icon>
                                </div>
                                <h4>{{ $t('connectivity.integrations.thirdParty.title') }}</h4>
                                <p>{{ $t('connectivity.integrations.thirdParty.description') }}</p>
                                <div class="integration-list">
                                    <div 
                                        v-for="(item, index) in thirdPartyItems" 
                                        :key="index" 
                                        class="integration-item"
                                    >
                                        <v-icon size="14">mdi-check-circle</v-icon>
                                        <span>{{ item }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Technical Highlights -->
                <div class="features-section">
                    <div class="section-header">
                        <h3>{{ $t('connectivity.features.title') }}</h3>
                        <p>{{ $t('connectivity.features.subtitle') }}</p>
                    </div>

                    <div class="features-grid">
                        <!-- Cloud-Native -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.cloudNative.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.cloudNative.title') }}</h4>
                            <p>{{ $t('connectivity.features.cloudNative.description') }}</p>
                        </div>

                        <!-- Enterprise Security -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.security.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.security.title') }}</h4>
                            <p>{{ $t('connectivity.features.security.description') }}</p>
                        </div>

                        <!-- High Performance -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.performance.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.performance.title') }}</h4>
                            <p>{{ $t('connectivity.features.performance.description') }}</p>
                        </div>

                        <!-- API-First Design -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.apiFirst.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.apiFirst.title') }}</h4>
                            <p>{{ $t('connectivity.features.apiFirst.description') }}</p>
                        </div>

                        <!-- Real-Time Updates -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.realtime.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.realtime.title') }}</h4>
                            <p>{{ $t('connectivity.features.realtime.description') }}</p>
                        </div>

                        <!-- Compliance Ready -->
                        <div class="feature-card">
                            <v-icon :icon="$t('connectivity.features.compliance.icon')" size="48" color="#1E5A8E"></v-icon>
                            <h4>{{ $t('connectivity.features.compliance.title') }}</h4>
                            <p>{{ $t('connectivity.features.compliance.description') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref } from 'vue';

const visible = ref(false);

function open() {
    visible.value = true;
}

function closeDialog() {
    visible.value = false;
}

defineExpose({ open });
</script>
<style scoped>
/* Card Base */
.connectivity-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.connectivity-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Section Styling */
.architecture-section,
.systems-section,
.features-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Architecture Flow */
.flow-wrapper {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.flow-level {
    margin-bottom: 0;
}

.level-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #f3f4f6;
    border-radius: 20px;
    margin-bottom: 20px;
}

.badge-number {
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}

.badge-text {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.flow-card {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 20px;
    padding: 32px;
    display: flex;
    gap: 24px;
    align-items: flex-start;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 2px solid transparent;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.flow-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.flow-card.user-card {
    border-color: rgba(139, 92, 246, 0.2);
}

.flow-card.user-card:hover {
    border-color: #8b5cf6;
}

.flow-card.app-card {
    border-color: rgba(30, 90, 142, 0.2);
}

.flow-card.app-card:hover {
    border-color: #1E5A8E;
}

.flow-card.platform-card {
    border-color: rgba(16, 185, 129, 0.2);
}

.flow-card.platform-card:hover {
    border-color: #10b981;
}

.card-icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.4s ease;
}

.flow-card:hover .card-icon-wrapper {
    transform: scale(1.1) rotate(-5deg);
}

.user-card .card-icon-wrapper {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.app-card .card-icon-wrapper {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.platform-card .card-icon-wrapper {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.card-content {
    flex: 1;
}

.card-content h4 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.card-content > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 16px 0;
}

.card-features {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.feature {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #f3f4f6;
    border-radius: 20px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.feature .v-icon {
    color: #10b981;
}

.tech-stack {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tech-badge {
    padding: 6px 12px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    color: white;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
}

.platform-metrics {
    display: flex;
    gap: 24px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 2px solid #f3f4f6;
}

.metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.metric-value {
    font-size: 24px;
    font-weight: 800;
    color: #10b981;
}

.metric-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

/* Flow Connector */
.flow-connector {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 24px 0;
}

.connector-line {
    width: 2px;
    height: 60px;
    background: linear-gradient(180deg, #1E5A8E 0%, rgba(30, 90, 142, 0.3) 100%);
}

.connector-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: white;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #1E5A8E;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
}

.connector-arrow {
    color: #1E5A8E;
    margin-top: -8px;
    animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
}

/* System Cards */
.system-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 2px solid transparent;
}

.system-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.system-card.microsoft:hover { border-color: #0078d4; }
.system-card.enterprise:hover { border-color: #10b981; }
.system-card.iot:hover { border-color: #ec4899; }
.system-card.third-party:hover { border-color: #f59e0b; }

.system-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    transition: transform 0.4s ease;
}

.system-card:hover .system-icon {
    transform: scale(1.1) rotate(-5deg);
}

.system-card.microsoft .system-icon {
    background: linear-gradient(135deg, #0078d4 0%, #00bcf2 100%);
}

.system-card.enterprise .system-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.system-card.iot .system-icon {
    background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
}

.system-card.third-party .system-icon {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
}

.system-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.system-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.integration-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.integration-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.integration-item .v-icon {
    color: #10b981;
}

/* Feature Cards */
.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
}

.feature-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    text-align: center;
    transition: all 0.3s ease;
    border: 2px solid #f3f4f6;
}

.feature-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #1E5A8E;
}

.feature-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 16px 0 8px 0;
}

.feature-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .connectivity-container {
        padding: 32px 24px;
    }

    .flow-wrapper {
        padding: 32px 24px;
    }

    .flow-card {
        flex-direction: column;
        text-align: center;
    }

    .card-features {
        justify-content: center;
    }

    .platform-metrics {
        justify-content: center;
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .connectivity-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .flow-wrapper {
        padding: 24px 16px;
    }

    .features-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .connectivity-container {
        padding: 20px 12px;
    }

    .flow-wrapper {
        padding: 20px 12px;
    }

    .flow-card {
        padding: 20px;
    }

    .platform-metrics {
        flex-direction: column;
        gap: 16px;
    }
}

/* Scrollbar */
.connectivity-container::-webkit-scrollbar {
    width: 8px;
}

.connectivity-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.connectivity-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.connectivity-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>